import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BOOK_URL } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_MESSAGE = {
  role: "assistant",
  content:
    "Coucou ✨ je suis l'assistante de Lagigafam. Une question sur le BBL Natural Sculpt, le drainage lymphatique ou l'anti burn-out ? 💖",
};

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, open, loading]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input.trim() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMsg.content,
        session_id: sessionId,
      });
      setSessionId(res.data.session_id);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: res.data.reply },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Oups, j'ai eu un petit bug ✨ Réserve directement ici : " + BOOK_URL,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    "C'est quoi BBL Natural Sculpt ?",
    "Comment réserver ?",
    "Tarif drainage lymphatique ?",
  ];

  return (
    <>
      {!open && (
        <button
          data-testid="chatbot-open-button"
          onClick={() => setOpen(true)}
          className="fixed z-50 bottom-5 right-5 md:bottom-7 md:right-7 group"
          aria-label="Ouvrir le chat"
        >
          <span className="absolute inset-0 rounded-full bg-pink-600/50 animate-ping" />
          <span className="relative flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white pl-5 pr-6 h-16 rounded-full shadow-[0_15px_40px_rgba(255,0,127,0.5)] font-bold transition-transform group-hover:scale-105">
            <MessageCircle size={22} />
            <span className="hidden sm:inline font-display uppercase tracking-wider text-sm">
              Chat avec Lagigafam
            </span>
          </span>
        </button>
      )}

      {open && (
        <div
          data-testid="chatbot-window"
          className="fixed z-50 bottom-5 right-5 md:bottom-7 md:right-7 w-[calc(100vw-2.5rem)] sm:w-[400px] h-[600px] max-h-[85vh] bg-white rounded-[2rem] shadow-[0_30px_80px_-10px_rgba(255,0,127,0.5)] border-2 border-pink-200 flex flex-col overflow-hidden"
        >
          <div className="bg-pink-600 text-white p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-white/15 flex items-center justify-center">
                <Sparkles size={20} />
              </div>
              <div>
                <p className="font-display font-black text-lg leading-none">
                  Lagigafam AI
                </p>
                <p className="text-xs opacity-80 mt-1">
                  En ligne • Réponse instantanée
                </p>
              </div>
            </div>
            <button
              data-testid="chatbot-close-button"
              onClick={() => setOpen(false)}
              className="h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
              aria-label="Fermer le chat"
            >
              <X size={18} />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-pink-50/40 no-scrollbar"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${i}`}
                className={`flex ${
                  m.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-pink-600 text-white rounded-br-md"
                      : "bg-white text-neutral-800 border border-pink-100 rounded-bl-md"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl px-4 py-3 border border-pink-100 rounded-bl-md">
                  <span className="dot-bounce text-pink-600 text-xl">
                    <span>•</span>
                    <span>•</span>
                    <span>•</span>
                  </span>
                </div>
              </div>
            )}
          </div>

          {messages.length <= 2 && (
            <div className="px-3 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  data-testid={`chat-suggestion-${i}`}
                  onClick={() => {
                    setInput(s);
                  }}
                  className="shrink-0 text-xs font-semibold bg-white border border-pink-200 text-pink-700 rounded-full px-3 py-2 hover:bg-pink-100 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={send}
            className="p-3 border-t border-pink-100 bg-white flex items-center gap-2"
          >
            <Input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Écris ton message..."
              className="h-12 rounded-full border-pink-200 focus-visible:ring-pink-500 focus-visible:ring-2 px-5"
              disabled={loading}
            />
            <Button
              data-testid="chatbot-send-button"
              type="submit"
              disabled={loading || !input.trim()}
              className="h-12 w-12 rounded-full bg-pink-600 hover:bg-pink-700 text-white shrink-0 p-0"
              aria-label="Envoyer"
            >
              <Send size={18} />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
